import React, { useState, useEffect } from "react";
import apiFetch, { getHeadquarters } from "../helpers/requests";
import { useTranslation } from "react-i18next";

const ChangeLicenseDataModal = ({
  setShowChangeLicenseDataModal,
  license,
  handleChangeLicenseData,
  headquarterId: initialHeadquarterId,
}) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [expirationDate, setExpirationDate] = useState("");
  const [headquarters, setHeadquarters] = useState<any[]>([]);
  const [selectedHQId, setSelectedHQId] = useState(license.headquarterId || "");
  const [isLoadingHQ, setIsLoadingHQ] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    const fetchHQs = async () => {
      setIsLoadingHQ(true);
      try {
        const data = await getHeadquarters();
        setHeadquarters(data);
      } catch (error) {
        console.error("Error fetching headquarters:", error);
      } finally {
        setIsLoadingHQ(false);
      }
    };

    fetchHQs();
  }, []);

  useEffect(() => {
    if (license && license.expirationDate) {
      let date;
      if (license.expirationDate.seconds || license.expirationDate._seconds) { // Firestore Timestamp
        date = new Date((license.expirationDate.seconds || license.expirationDate._seconds) * 1000);
      } else { // String from previous edit
        date = new Date(license.expirationDate);
      }

      if (date && !isNaN(date.getTime())) {
        const formattedDate = date.toISOString().split('T')[0];
        setExpirationDate(formattedDate);
      } else {
        setExpirationDate('');
      }
    } else {
      setExpirationDate('');
    }
    
    if (license.headquarterId) {
        setSelectedHQId(license.headquarterId);
    }
  }, [license]);

  const updateLicenseData = async (licenseId, licenseData) => {
    try {
      await apiFetch(`/updateLicense/${licenseId}`, {
        method: "POST",
        body: JSON.stringify(licenseData),
      });
    } catch (error) {
      console.error("Error updating license data:", error);
      // The API will throw an error object, likely with a 'message' property
      throw error; // Re-throw to be caught by handleSaveDataClick
    }
  };

  const isSaveDisabled = !expirationDate || !selectedHQId;

  const handleSaveDataClick = async () => {
    setFormSubmitted(true);

    if (isSaveDisabled) {
      return;
    }

    const backendLicenseData = {
      expirationDate: expirationDate === "" ? null : expirationDate,
      headquarterId: selectedHQId
    };

    try {
      await updateLicenseData(license.id, backendLicenseData);

      // Find selected HQ name for the UI update
      const selectedHQ = headquarters.find(h => h.id === selectedHQId);

      const frontendLicenseData = {
        ...backendLicenseData,
        headquarterName: selectedHQ ? selectedHQ.name : license.headquarterName
      };
      
      handleChangeLicenseData({ ...license, ...frontendLicenseData });      
      setShowChangeLicenseDataModal(false); // Close modal after saving
    } catch (error) {
      console.error("Failed to save license data:", error);
    }
  };

  return (
    <div className="confirmation-modal">
      <div className="modal-content">
        <div style={{ textAlign: "center", fontSize: "1.3em" }}>
          {t("Edit_License")}
        </div>
        <form className="m-4">
          
          {/* Headquarter Selection */}
          <div className="mb-3" style={{ marginTop: "2vh" }}>
            <label htmlFor="hqSelect" style={{ marginLeft: "20px", color: "#404040", fontWeight: "bold" }}>
              {t("Headquarter")}
            </label>
            <select
              id="hqSelect"
              className="form-control"
              value={selectedHQId}
              onChange={(e) => setSelectedHQId(e.target.value)}
              required
              style={{
                backgroundColor: "#CCCCCC",
                borderRadius: "20px",
                color: "#404040",
                height: "5vh",
                maxHeight: "40px",
                fontSize: "1em",
                paddingLeft: "20px",
              }}
            >
              <option value="" disabled hidden>
                {t("Select_Headquarter")}
              </option>
              {headquarters.map((hq) => (
                <option key={hq.id} value={hq.id}>
                  {hq.name}
                </option>
              ))}
            </select>
            {formSubmitted && !selectedHQId && (
              <label style={{ marginLeft: "20px", color: "#A63939", fontSize: "0.8em" }}>
                {t("Headquarter_is_required")}
              </label>
            )}
          </div>

          <div className="mb-3" style={{ marginTop: "2vh" }}>
            <label htmlFor="expirationDateInput" style={{ marginLeft: "20px", color: "#404040", fontWeight: "bold" }}>
              {t("Expiration_Date")}
            </label>
            <input
              type="date"
              className="form-control"
              id="expirationDateInput"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              style={{ backgroundColor: "#CCCCCC", borderRadius: "20px", color: "#404040", height: "5vh", maxHeight: "40px", fontSize: "1em", paddingLeft: "20px" }}
            />
            {formSubmitted && !expirationDate && (
              <label style={{ marginLeft: "20px", color: "#A63939", fontSize: "0.8em" }}>
                {t("Expiration_date_is_required")}
              </label>
            )}
          </div>

          <div className="button-container">
            <button
              type="button"
              className="buttonCancel btn btn-primary"
              onClick={() => {
                setShowChangeLicenseDataModal(false);
                setFormSubmitted(false);
              }}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary buttonAcceptGreen"
              onClick={handleSaveDataClick}
              disabled={isLoadingHQ}
            >
              {t("Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangeLicenseDataModal;
